import { Key, diatonicSet, keyDistance } from './key';
import { HarmonyEvent } from './piece';
import { Chord } from './roman';
import { mod } from './pitch';

/**
 * Deterministic tonal-tension model.
 *
 * Tension is a weighted sum of five audible ingredients, each computed from the
 * notes and the harmonic label:
 *
 *   function     how strongly the chord points elsewhere (tonic < predominant < dominant)
 *   dissonance   rough intervals actually sounding (semitones, tritones, whole tones)
 *   distance     how far the local key sits from home on the circle of fifths
 *   colour       notes foreign to the local key, and non-chord tones such as suspensions
 *   pedal        a held dominant bass builds tension bar by bar; a tonic pedal releases it
 *
 * The weights are published in the Method page and tested in src/music/__tests__.
 */

export interface TensionParts {
  fn: number;
  dissonance: number;
  distance: number;
  colour: number;
  pedal: number;
}

export interface TensionResult {
  value: number;
  parts: TensionParts;
}

export const WEIGHTS = {
  fn: 0.52,
  dissonance: 0.3,
  distancePerFifth: 0.09,
  distanceCap: 0.27,
  chromaticPerNote: 0.06,
  chromaticCap: 0.18,
  nonChordTone: 0.12,
  nonChordCap: 0.24,
  dominantPedalPerBar: 0.022,
  dominantPedalCap: 0.18,
  tonicPedalPerBar: 0.035,
  tonicPedalCap: 0.14,
  sixFour: 0.12,
  restBonus: -0.05,
};

/** Rough-interval measure of a pitch-class set, 0–1. */
export function dissonanceOf(pcs: number[]): number {
  const set = [...new Set(pcs.map((p) => mod(p, 12)))];
  let raw = 0;
  for (let i = 0; i < set.length; i++) {
    for (let j = i + 1; j < set.length; j++) {
      let ic = mod(set[j] - set[i], 12);
      if (ic > 6) ic = 12 - ic;
      raw += ic === 1 ? 1 : ic === 6 ? 0.8 : ic === 2 ? 0.45 : 0;
    }
  }
  return Math.min(1, raw / 1.6);
}

function rootDegree(chord: Chord): number {
  return mod(chord.root.letter - chord.localKey.tonic.letter, 7) + 1;
}

/** How strongly a chord's function pulls away from rest, 0–1. */
export function functionPull(chord: Chord): number {
  if (chord.cadential) return 0.75;
  const deg = rootDegree(chord);
  const isTonic = chord.fn === 'T' && deg === 1 && !chord.applied;
  if (chord.fn === 'T') {
    if (isTonic) return chord.inversion === 2 ? 0.3 : chord.inversion === 1 ? 0.08 : 0;
    return 0.14;
  }
  if (chord.fn === 'PD') {
    const base = chord.special === 'N' ? 0.5 : chord.special ? 0.58 : deg === 2 ? 0.4 : 0.34;
    return base + (chord.seventh ? 0.05 : 0);
  }
  let d = chord.triad === 'diminished' ? 0.72 : 0.62;
  if (chord.seventh === 'dim7') d = 0.84;
  else if (chord.seventh === 'hdim7') d = 0.76;
  else if (chord.seventh === 'dom7') d = 0.72;
  return Math.min(1, d + (chord.applied ? 0.04 : 0));
}

export interface TensionContext {
  chord: Chord;
  /** MIDI numbers sounding under this harmony. */
  sounding: number[];
  /** Key of the region the chord sits in (for colour and distance). */
  regionKey: Key;
  homeKey: Key;
  pedal?: { kind: 'dominant' | 'tonic'; barsIn: number; midi: number };
}

export function chordTension(ctx: TensionContext): TensionResult {
  const { chord, sounding, regionKey, homeKey, pedal } = ctx;
  const W = WEIGHTS;
  let fn = functionPull(chord);
  if (pedal?.kind === 'tonic') fn *= 0.5;

  const pcs = sounding.map((m) => mod(m, 12));
  const diss = dissonanceOf(pcs);

  const distance = Math.min(W.distanceCap, keyDistance(regionKey, homeKey) * W.distancePerFifth);

  const diatonic = diatonicSet(regionKey);
  const distinct = [...new Set(pcs)];
  const chromatic = Math.min(W.chromaticCap, distinct.filter((p) => !diatonic.has(p)).length * W.chromaticPerNote);
  const pedalPc = pedal ? mod(pedal.midi, 12) : -1;
  const nonChord = distinct.filter((p) => !chord.pcs.includes(p) && p !== pedalPc).length;
  const nct = Math.min(W.nonChordCap, nonChord * W.nonChordTone);
  const sixFour = chord.inversion === 2 && !chord.seventh ? W.sixFour : 0;

  let pedalPart = 0;
  if (pedal?.kind === 'dominant') pedalPart = Math.min(W.dominantPedalCap, pedal.barsIn * W.dominantPedalPerBar);
  if (pedal?.kind === 'tonic') pedalPart = -Math.min(W.tonicPedalCap, pedal.barsIn * W.tonicPedalPerBar);

  const atRest = chord.fn === 'T' && rootDegree(chord) === 1 && chord.inversion === 0 && nonChord === 0 && !chord.applied;

  const parts: TensionParts = {
    fn: W.fn * fn,
    dissonance: W.dissonance * diss,
    distance,
    colour: chromatic + nct + sixFour,
    pedal: pedalPart,
  };
  const sum = parts.fn + parts.dissonance + parts.distance + parts.colour + parts.pedal + (atRest ? W.restBonus : 0);
  return { value: Math.max(0, Math.min(1, sum)), parts };
}

export interface TensionPoint {
  t: number;
  v: number;
  event: number;
}

/** Region key lookup: the key of the region containing a bar, or the event's own key. */
export type RegionKeyFn = (bar: number, fallback: Key) => Key;

/** Tension for every harmony event, plus a sampled curve (two points per event) for drawing. */
export function tensionCurve(
  events: HarmonyEvent[],
  homeKey: Key,
  regionKeyOf: RegionKeyFn = (_bar, k) => k,
): { perEvent: TensionResult[]; points: TensionPoint[] } {
  const perEvent = events.map((ev) =>
    chordTension({
      chord: ev.chord,
      sounding: ev.sounding.map((n) => n.midi),
      regionKey: regionKeyOf(ev.bar, ev.key),
      homeKey,
      pedal: ev.pedal,
    }),
  );
  const points: TensionPoint[] = [];
  events.forEach((ev, i) => {
    const dur = ev.end - ev.start;
    const inset = Math.min(dur * 0.3, 1.2);
    points.push({ t: ev.start + inset, v: perEvent[i].value, event: i });
    points.push({ t: ev.end - inset, v: perEvent[i].value, event: i });
  });
  if (events.length) {
    points.unshift({ t: events[0].start, v: perEvent[0].value, event: 0 });
    points.push({ t: events[events.length - 1].end, v: perEvent[perEvent.length - 1].value, event: events.length - 1 });
  }
  return { perEvent, points };
}

/** Tension of a free-standing chord (the Walk instrument). */
export function standaloneTension(chord: Chord, midi: number[], homeKey: Key): number {
  return chordTension({ chord, sounding: midi, regionKey: chord.key, homeKey }).value;
}

export function pcsOf(midi: number[]): number[] {
  return [...new Set(midi.map((m) => mod(m, 12)))];
}
